import { toTimestampMs } from 'utils/timeUtils';

/** @typedef {import('../types/USGSDeviceGraph_types').GraphDataPoint} GraphDataPoint */
/** @typedef {import('../types/USGSDeviceGraph_types').GraphDatasetMeta} GraphDatasetMeta */

/**
 * Resolve a data row id (`did`) from ECharts event `params` using dataset metadata.
 *
 * The function attempts multiple lookup strategies:
 * - use `params.axisValue` or `params.value[0]` as a timestamp and match against
 *   `datasetMeta.byTimestampMap` (supporting numeric and string keys)
 * - fall back to scanning `datasetMeta.data` for a data point whose `ts` matches
 *   the resolved timestamp
 *
 * @param {any} params - Event params provided by ECharts (may contain `axisValue`, `value`, `dataIndex`, etc.)
 * @param {GraphDatasetMeta | Record<string, any>} datasetMeta - Dataset `_meta` attached to the chart dataset (may include `byTimestampMap` and `data`)
 * @returns {number | string | null} Returns the `did` value if found, otherwise `null`.
 */
export function resolveDataRowIdFromParams(params, datasetMeta) {
  if (!params || !datasetMeta) return null;
  try {
    const timestampRaw = params.axisValue ?? (Array.isArray(params.value) ? params.value[0] : null);
    const timestampNum = toTimestampMs(timestampRaw);
    if (!Number.isFinite(timestampNum) || Number.isNaN(timestampNum)) return null;

    const byTimestampMap = datasetMeta?.byTimestampMap;
    if (byTimestampMap) {
      let raw =
        byTimestampMap.get(timestampNum) ||
        byTimestampMap.get(String(timestampNum)) ||
        byTimestampMap.get(String(timestampRaw));
      if (!raw && Array.isArray(datasetMeta?.data)) {
        raw = datasetMeta.data.find((p) => {
          try {
            return p && toTimestampMs(p.ts) === timestampNum;
          } catch (e) {
            return false;
          }
        });
      }
      return raw?.did ?? null;
    }
  } catch (e) {
    // ignore
  }
  return null;
}

/**
 * Update the current URL query params with `uid` and optional `did` and navigate.
 *
 * This helper builds a new search string and calls the provided `navigate`
 * function (from `react-router-dom`) with `replace: true` so the change does not
 * create a new history entry.
 *
 * This allows linking directly to a specific device and data point in the UI.
 *
 * @param {number|string} deviceId - Device id to set as `uid`.
 * @param {number|string|null|undefined} dataRowId - Optional data-row id to set as `did`.
 * @param {(to: string, options?: { replace?: boolean }) => void} navigate - `navigate` function from `react-router-dom`.
 * @param {{ pathname: string, search?: string }} location - `location` object (expects `pathname` and `search`).
 * @returns {void}
 */
export function setUidDidInUrl(deviceId, dataRowId, navigate, location) {
  if (!deviceId) return;

  if (typeof navigate !== 'function' || !location) {
    console.warn('setUidDidInUrl requires navigate and location; skipping URL update');
    return;
  }

  const sp = new URLSearchParams(location.search);
  sp.set('uid', String(deviceId));
  if (dataRowId) sp.set('did', String(dataRowId));
  const newSearch = `?${sp.toString()}`;
  const newUrl = `${location.pathname}${newSearch}`;
  navigate(newUrl, { replace: true });
}
